/**
 * The discard confirmation inside the import modal (TMMAPP-0102; DLG-005, ui-patterns §12): it renders
 * in place of the modal's own content, never as a second dialog. The step portals a `ConfirmPanel`
 * into the modal's panel host while `open`, and reports `onConfirming` so the modal hides its title,
 * steps and footer for the duration. `Esc` is routed to Cancel here (unless busy), before the host
 * dialog can treat it as "close the whole import". Without a host (a step used on its own) it falls
 * back to a `ConfirmDialog`.
 */
import { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { Box } from '@mui/material';
import { ConfirmDialog, ConfirmPanel, type ConfirmPanelProps } from '@/components/ConfirmDialog';

type DiscardPanelProps = Omit<ConfirmPanelProps, 'titleId' | 'consequenceId' | 'focusHeading'> & {
  open: boolean;
  /** The modal's panel host: `undefined` outside the modal, `null` while it mounts. */
  panelHost: HTMLElement | null | undefined;
  /** Must be a stable function (a state setter). */
  onConfirming: ((confirming: boolean) => void) | undefined;
};

/** The id the modal points `aria-labelledby` at while a panel is showing. */
export const DISCARD_TITLE_ID = 'import-discard-title';

export function DiscardPanel({ open, panelHost, onConfirming, ...panelProps }: DiscardPanelProps) {
  const consequenceId = useId();
  const inModal = panelHost !== undefined;
  const active = open && inModal;

  useEffect(() => {
    if (!active) return undefined;
    onConfirming?.(true);
    return () => onConfirming?.(false);
  }, [active, onConfirming]);

  if (!open) return null;
  if (!inModal) return <ConfirmDialog {...panelProps} />;
  if (panelHost === null) return null;
  return createPortal(
    <Box
      sx={{ display: 'contents' }}
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        if (panelProps.busy !== true) panelProps.onCancel();
      }}
    >
      <ConfirmPanel
        {...panelProps}
        titleId={DISCARD_TITLE_ID}
        consequenceId={consequenceId}
        focusHeading
      />
    </Box>,
    panelHost,
  );
}
